import type { SiteProductKind } from '../constants/siteConfig';

/** Paths that exist only on the car sites. */
const CAR_ONLY_PREFIXES = [
  '/cars/',
  '/trips-as-additional-driver',
  '/dealer',
  '/dealership',
  '/bookedmycars',
];

/** Paths that exist only on the stays site (spec 016). */
const STAY_ONLY_PREFIXES = ['/stays', '/wishlists'];

/** Paths that exist only on the services site (spec 019). */
const SERVICE_ONLY_PREFIXES = ['/services', '/accept-worker-invite'];

/** What each kind of site must not render. */
const FOREIGN_PREFIXES: Record<SiteProductKind, readonly string[]> = {
  car: [...STAY_ONLY_PREFIXES, ...SERVICE_ONLY_PREFIXES],
  stay: [...CAR_ONLY_PREFIXES, ...SERVICE_ONLY_PREFIXES],
  service: [...CAR_ONLY_PREFIXES, ...STAY_ONLY_PREFIXES],
};

const matches = (pathname: string, prefix: string) =>
  pathname === prefix || pathname.startsWith(prefix.endsWith('/') ? prefix : `${prefix}/`);

/**
 * Where to send a path that belongs to another kind of site, or null to stay.
 * A car link opened on the stays or services site (or the reverse) goes to the
 * home page instead of rendering car screens for a place or a worker.
 */
export function productPathRedirect(pathname: string, kind: SiteProductKind): string | null {
  return FOREIGN_PREFIXES[kind].some(prefix => matches(pathname, prefix)) ? '/' : null;
}

/**
 * A booking's page for its guest or host. On the stays site every booking is a stay
 * (spec 016 W18, W19), and the car pages there redirect home.
 */
export function bookingDetailPath(
  role: 'guest' | 'host',
  bookingId: string,
  kind: SiteProductKind,
): string {
  if (kind === 'stay') {
    return role === 'host'
      ? `/stays/host/booking/${bookingId}`
      : `/stays/guest/booking/${bookingId}`;
  }
  if (kind === 'service') {
    // Job pages arrive with spec 019 W16 and W21.
    return role === 'host' ? `/services/provider/jobs/${bookingId}` : `/services/jobs/${bookingId}`;
  }
  return role === 'host' ? `/cars/host/booking/${bookingId}` : `/cars/guest/booking/${bookingId}`;
}

/**
 * A damage claim's page for its guest or host. On the stays site claims are the host's
 * Resolution Center requests (spec 016 W25); the car host pages there redirect home.
 */
export function claimDetailPath(
  role: 'guest' | 'host',
  claimId: string,
  kind: SiteProductKind,
): string {
  return `${claimListPath(role, kind)}/${claimId}`;
}

export function claimListPath(role: 'guest' | 'host', kind: SiteProductKind): string {
  if (role === 'guest') return '/guest/damage-claims';
  if (kind === 'service') return '/services/provider/claims';
  return kind === 'stay' ? '/stays/host/claims' : '/cars/host/damage-claims';
}
